import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import { Package } from "lucide-react";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setLoading(false);
          return;
        }

        // 1. ORDERS
        const { data: ordersData } = await supabase
          .from("orders")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        // 2. RETURNS
        const { data: returnsData } = await supabase
          .from("returns")
          .select("*");

        // 3. MERGE
        const merged = (ordersData || []).map((order) => ({
          ...order,
          returns: returnsData?.filter(
            (r) => r.order_id === order.id
          ) || [],
        }));

        setOrders(merged);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const canReturn = (deliveredAt) => {
    if (!deliveredAt) return false;
    const delivered = new Date(deliveredAt);
    const today = new Date();
    const diffDays = (today - delivered) / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  };

  const getStatusColor = (status) => {
    if (status === "pending") return "bg-amber-100 text-amber-700 border-amber-200";
    if (status === "processing") return "bg-blue-100 text-blue-700 border-blue-200";
    if (status === "shipped") return "bg-purple-100 text-purple-700 border-purple-200";
    return "bg-green-100 text-green-700 border-green-200";
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-serif text-5xl text-charcoal mb-14 text-center">
          My Orders
        </h1>

        {loading && <p className="text-center">Loading...</p>}

        {!loading && orders.length === 0 && (
          <div className="text-center text-gray-500">
            <Package className="mx-auto mb-4 opacity-60" size={42} />
            You haven’t placed any orders yet.
          </div>
        )}

        <div className="space-y-8">
          {orders.map((order) => (
            <div
              key={order.id}
              onClick={() => navigate(`/order-details/${order.id}`)}
              className="bg-white border rounded-2xl p-8 shadow-sm hover:shadow-lg cursor-pointer"
            >
              <div className="flex justify-between">
                <div>
                  <h2 className="text-2xl font-serif">Order #{order.id}</h2>
                  <p className="text-sm text-gray-500">
                    {new Date(order.created_at).toLocaleDateString()}
                  </p>
                </div>

                <span className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider border ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>

              <div className="flex justify-between items-center mt-6">
                <p className="text-lg font-medium text-[#ba8245]">
                  ₹{order.total_amount}
                </p>

                {/* RETURN LOGIC */}
                {order.status === "delivered" &&
                  canReturn(order.delivered_at) && (
                    order.returns.length > 0 ? (
                      <span className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        ↺ Return Initiated
                      </span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/return/${order.id}`);
                        }}
                        className="text-sm border border-red-300 text-red-600 px-5 py-2 rounded-full hover:bg-red-50"
                      >
                        Return Item
                      </button>
                    )
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Orders;